<template>
  <div class="wizard-step-revisao">
    <div class="step-header">
      <h3>{{ t('Conclusão') }}:</h3>
      <h4>
        {{ t('Confirme os valores e forma de pagamento e clique em FINALIZAR') }}.
      </h4>
    </div>

    <!-- Services summary -->
    <services-review-list
      :services="allServices"
      @edit-service="handleEditService"
    />

    <!-- Review section -->
    <div class="callout bg-gray">
      <!-- Total value -->
      <div class="text-right">
        <span class="small">{{ t('Total do compromisso') }}: </span>
        <h2 style="margin-top: 0px">
          <b>{{ n(totalValue || 0, 'currency') }}</b>
        </h2>
      </div>

      <!-- Recebimentos Summary -->
      <div class="form-row">
        <div class="form-label">
          <b>{{ t('Total de Recebimentos') }}:</b>
        </div>
        <div class="form-input">
          <div style="display: flex; align-items: center; gap: 10px;">
            <span style="font-size: 1.2em; font-weight: bold;">
              {{ n(totalRecebimentos || 0, 'currency') }}
            </span>
            <Button
              data-testid="agenda-wizard-revisao-adicionar-recebimento-btn"
              :label="t('Adicionar Recebimento')"
              icon="pi pi-plus"
              @click="openRecebimentoWizard"
              severity="success"
              size="small"
            />
          </div>
        </div>
      </div>

      <!-- Origin -->
      <div class="form-row">
        <div class="form-label">
          <b>{{ t('Origem') }}:</b>
        </div>
        <div class="form-input">
          <SelectList
            elem-id="origem"
            v-model="formData.origem"
            :itens-select="origensSelectList"
            :placeholder="t('Selecione uma origem')"
            :auto-select-first="false"
            ng-required="true"
            :validation="() => !!formData.origem"
          />
        </div>
      </div>

      <!-- Observations -->
      <div class="form-row">
        <div class="form-label">
          <b>{{ t('Observações') }}:</b>
        </div>
        <div class="form-input">
          <InputRichText
            elem-id="observacoes"
            v-model="formData.observacoes"
            :placeholder="t('Adicione observações sobre o serviço (opcional)')"
            show-toolbar="true"
            min-height="150px"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import ServicesReviewList from './ServicesReviewList.vue';
import SelectList from '../../../inputs/SelectList.vue';
import InputRichText from '../../../inputs/InputRichText.vue';
import { useCompromisso } from '../../../composables/useCompromisso';
import { useWizardServicos } from '../../../composables/useWizardServicos';
import { useOrcamento } from '../../../composables/useOrcamento';
import { useRecebimento } from '../../../composables/useRecebimento';
import { eventBus } from '../../../eventBus';
import { log } from '../../../utils/DebugLogger';
import type { Agenda_BuscarFormasPagamentoRow } from '../../../contracts/Agenda_BuscarFormasPagamentoRow';
import type { Recebimentos_GetrecebimentosRow } from '../../../contracts/Recebimentos_GetrecebimentosRow';

type Id = number | string;

type FormaPagamento = Agenda_BuscarFormasPagamentoRow;

interface OrigemItem {
  ID: string;
  Name: unknown;
}

// Recebimento row shape comes from the contract — Recebimentos_GetrecebimentosRow.
// fetchRecebimentosByCompromisso() in useRecebimento is wired to
// responseContract: Recebimentos_GetrecebimentosbyownerandowneridResponse, whose
// dados is Recebimentos_GetrecebimentosRow[].
type Recebimento = Recebimentos_GetrecebimentosRow;

interface CompromissoShape {
  compromisso_id?: number;
  compromisso_orcamento?: Id;
  compromisso_franquia_id?: Id;
  compromisso_forma_pagamento?: string;
  compromisso_numero_parcelas?: string;
  compromisso_origem?: number | string | null;
  orcamento_origem?: number | string | null;
  compromisso_titulo?: string;
  compromisso_cor_fundo?: string;
  compromisso_data_hora_inicio?: string;
  compromisso_data_hora_fim?: string;
  servicos?: Array<{ compromisso_servico_id?: number; compromisso_servico_status?: number; [key: string]: unknown }>;
  [key: string]: unknown;
}

interface FormData {
  formaPagamento: string;
  numeroParcelas: string;
  origem: string;
  observacoes: string;
}

interface Props {
  compromisso: CompromissoShape;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'next-step': [];
  'refresh-compromisso': [];
}>();

const { t, n } = useI18n();
const { fetchFormasPagamento } = useCompromisso();
const { finalizeWizard, calculateTotalValue } = useWizardServicos();
const { fetchOrigens } = useOrcamento();
const { fetchRecebimentosByCompromisso } = useRecebimento();

// State
const formasPagamento = ref<FormaPagamento[]>([]);
const origens = ref<OrigemItem[]>([]);
const parcelas = ref<number[]>([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);
const recebimentos = ref<Recebimento[]>([]);

// A origem mora no ORÇAMENTO: é nele que este passo grava (orcamento_origem) e é dele que os
// relatórios leem. compromisso_origem é só a cópia tirada no agendamento — vazia em compromisso
// vindo do legado ou de orçamento que ganhou origem depois —, e por ela o select abria em branco
// mesmo com a origem já registrada (chamado 2026-0048). 0 também é "sem origem".
// E "em branco" não era o que se via: o SelectList escolhe sozinho a PRIMEIRA opção quando não
// há valor, então o passo abria com a primeira origem da lista e finalizar gravava essa por cima
// da verdadeira. Por isso o select daqui não se auto-preenche: sem origem, a pessoa escolhe.
function origemJaRegistrada(compromisso: CompromissoShape): string {
  for (const valor of [compromisso.orcamento_origem, compromisso.compromisso_origem]) {
    if (Number(valor) > 0) return String(valor);
  }
  return '';
}

const formData = ref<FormData>({
  formaPagamento: String(props.compromisso.compromisso_forma_pagamento ?? ''),
  numeroParcelas: String(props.compromisso.compromisso_numero_parcelas ?? ''),
  origem: origemJaRegistrada(props.compromisso),
  observacoes: ''
});

// Computed
const allServices = computed(() => {
  if (!props.compromisso?.servicos) return [];
  return props.compromisso.servicos;
});

const totalValue = computed((): number => {
  return calculateTotalValue(props.compromisso);
});

const totalRecebimentos = computed((): number => {
  return recebimentos.value.reduce((sum: number, recebimento: Recebimento) => {
    return sum + (recebimento.recebimento_valor || 0);
  }, 0);
});

const paymentMethodAcceptsInstallments = computed((): boolean => {
  if (!formData.value.formaPagamento || !formasPagamento.value.length) return false;
  const selectedMethod = formasPagamento.value.find(
    (f) => String(f.forma_pagamento_id) === String(formData.value.formaPagamento)
  );
  return !!(selectedMethod && Number(selectedMethod.forma_pagamento_aceita_parcelado) === 1);
});

const canFinalize = computed((): boolean => {
  return !!formData.value.origem;
});

const formasPagamentoSelectList = computed(() => {
  log('WizardStep3Revisao', 'formasPagamentoSelectList', 'Mapping formasPagamento', formasPagamento.value);
  return formasPagamento.value.map((forma) => ({
    ID: forma.forma_pagamento_id,
    Name: forma.forma_pagamento_nome
  }));
});

const parcelasSelectList = computed(() => {
  return parcelas.value.map((num) => ({
    ID: String(num),
    Name: String(num)
  }));
});

const origensSelectList = computed(() => {
  log('WizardStep3Revisao', 'origensSelectList', 'Raw origens data', origens.value);
  return origens.value as any;
});

// Methods
const loadFormasPagamento = async (): Promise<void> => {
  try {
    log('WizardStep3Revisao', 'loadFormasPagamento', 'Loading payment methods');
    formasPagamento.value = await fetchFormasPagamento(Number(props.compromisso?.compromisso_franquia_id) || null);
    log('WizardStep3Revisao', 'loadFormasPagamento', 'Payment methods loaded', formasPagamento.value);
  } catch (error) {
    log('WizardStep3Revisao', 'loadFormasPagamento', 'Error loading payment methods', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar formas de pagamento'));
  }
};

const loadOrigens = async (): Promise<void> => {
  try {
    log('WizardStep3Revisao', 'loadOrigens', 'Loading origins');
    origens.value = await fetchOrigens();
    log('WizardStep3Revisao', 'loadOrigens', 'Origins loaded', origens.value);
  } catch (error) {
    log('WizardStep3Revisao', 'loadOrigens', 'Error loading origins', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar origens'));
  }
};

const loadRecebimentos = async (): Promise<void> => {
  if (!props.compromisso?.compromisso_id) {
    log('WizardStep3Revisao', 'loadRecebimentos', 'No compromisso ID, skipping');
    return;
  }
  try {
    log('WizardStep3Revisao', 'loadRecebimentos', 'Loading recebimentos for compromisso', props.compromisso.compromisso_id);
    const dados = await fetchRecebimentosByCompromisso(props.compromisso.compromisso_id);
    recebimentos.value = dados.map((item) => ({
      ...item,
      recebimento_valor: (item.recebimento_valor ?? 0) / 100
    }));
    log('WizardStep3Revisao', 'loadRecebimentos', 'Recebimentos loaded', recebimentos.value.length, 'items');
  } catch (error) {
    log('WizardStep3Revisao', 'loadRecebimentos', 'Error loading recebimentos', error);
    recebimentos.value = [];
  }
};

const openRecebimentoWizard = (): void => {
  log('WizardStep3Revisao', 'openRecebimentoWizard', 'Emitting eventBus to open wizard', {
    compromissoId: props.compromisso?.compromisso_id,
    franquiaId: props.compromisso?.compromisso_franquia_id
  });
  eventBus.emit('recebimento-wizard.open', {
    compromissoId: props.compromisso?.compromisso_id || 0,
    franquiaId: props.compromisso?.compromisso_franquia_id || 0
  });
};

const handleRecebimentoCreated = (recebimentoData: unknown): void => {
  log('WizardStep3Revisao', 'handleRecebimentoCreated', 'Recebimento created, reloading sum', recebimentoData);
  loadRecebimentos();
};

const emitReviewReady = (): void => {
  eventBus.emit('wizard-servicos.review.ready', {
    compromissoId: Number(props.compromisso?.compromisso_id) || null
  });
};

const initializeReviewStep = async (): Promise<void> => {
  await Promise.all([
    loadFormasPagamento(),
    loadOrigens(),
    loadRecebimentos()
  ]);
  emitReviewReady();
};

const handleEditService = (servicoId: Id): void => {
  log('WizardStep3Revisao', 'handleEditService', 'Emitting edit event for service', servicoId);
  eventBus.emit('compromisso-servico.open-edit', {
    compromissoServicoId: servicoId,
    compromissoId: props.compromisso.compromisso_id,
    orcamentoId: props.compromisso.compromisso_orcamento || 0
  });
};

const handleFinalize = async (): Promise<void> => {
  if (!canFinalize.value) {
    eventBus.emit('globalMessage.error', t('Por favor, preencha todos os campos obrigatórios'));
    return;
  }
  try {
    log('WizardStep3Revisao', 'handleFinalize', 'Finalizing wizard', formData.value);
    const compromissoData: Record<string, unknown> = {
      ...props.compromisso,
      compromisso_origem: formData.value.origem,
      compromisso_status: 'Realizado',
      title: props.compromisso.compromisso_titulo || null,
      backgroundColor: props.compromisso.compromisso_cor_fundo || null,
      start: props.compromisso.compromisso_data_hora_inicio || null,
      end: props.compromisso.compromisso_data_hora_fim || null
    };
    delete compromissoData.servicos;
    delete compromissoData.compromisso_status_original;
    delete compromissoData.localidade;
    delete compromissoData.cliente_location_id;
    delete compromissoData.cliente_location_type;
    delete compromissoData.cliente;
    delete compromissoData.nome_operador;
    delete compromissoData.avatar_operador;
    delete compromissoData.franquia_nome;
    delete compromissoData.compromisso_forma_pagamento_label;
    delete compromissoData.status_financeiro;

    const finalizeData = {
      compromisso: compromissoData,
      orcamento_origem: formData.value.origem,
      observacoesOperador: formData.value.observacoes
    };
    await finalizeWizard(finalizeData);
    eventBus.emit('globalMessage.success', t('Serviço finalizado com sucesso'));
    eventBus.emit('wizard-servicos.finalize-complete');
    emit('next-step');
    log('WizardStep3Revisao', 'handleFinalize', 'Wizard finalized successfully');
  } catch (error) {
    log('WizardStep3Revisao', 'handleFinalize', 'Error finalizing wizard', error);
    // A refused check-out is a failure, not a completion: say why, and tell the
    // modal to unlock the button — never `finalize-complete`, which the modal
    // treats as success (plan doutor-flow-test-coverage §3 S2).
    const reason = error instanceof Error && error.message ? error.message : '';
    eventBus.emit(
      'globalMessage.error',
      reason ? `${t('Erro ao finalizar serviço')}: ${reason}` : t('Erro ao finalizar serviço')
    );
    eventBus.emit('wizard-servicos.finalize-failed');
  }
};

const handleServicoSaved = (): void => {
  log('WizardStep3Revisao', 'handleServicoSaved', 'Service saved, emitting refresh event');
  emit('refresh-compromisso');
};

// Lifecycle
onMounted(() => {
  log('WizardStep3Revisao', 'mounted', 'Component mounted');
  initializeReviewStep();
  eventBus.on('compromisso.servico.saved', handleServicoSaved);
  eventBus.on('recebimento.created', handleRecebimentoCreated);
  eventBus.on('wizard-servicos.finalize', handleFinalize);
});

onUnmounted(() => {
  log('WizardStep3Revisao', 'unmounted', 'Component unmounted, cleaning up listeners');
  eventBus.off('compromisso.servico.saved', handleServicoSaved);
  eventBus.off('recebimento.created', handleRecebimentoCreated);
  eventBus.off('wizard-servicos.finalize', handleFinalize);
});

defineExpose({
  canFinalize,
  handleFinalize,
  formData,
  paymentMethodAcceptsInstallments
});
</script>

<style scoped>
.wizard-step-revisao {
  padding: 20px;
}

.step-header {
  margin-bottom: 30px;
}

.step-header h3 {
  font-size: 24px;
  margin-bottom: 10px;
}

.step-header h4 {
  font-size: 16px;
  color: #666;
  font-weight: normal;
}

.callout {
  margin-top: 30px;
  padding: 20px;
  border-radius: 4px;
  border-left: 4px solid #ddd;
}

.bg-gray {
  background-color: #f4f4f4;
}

.form-row {
  display: flex;
  margin-bottom: 15px;
  align-items: center;
  gap: 15px;
}

.form-label {
  flex: 0 0 33.333%;
  text-align: right;
}

.form-input {
  flex: 1;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .wizard-step-revisao {
    padding: 10px;
  }

  .step-header h3 {
    font-size: 20px;
  }

  .step-header h4 {
    font-size: 14px;
  }

  .form-row {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 20px;
  }

  .form-label {
    text-align: left;
    margin-bottom: 5px;
    flex: none;
  }

  .form-input {
    width: 100%;
  }
}
</style>
